"use client";
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { updateUser } from "../services/userService";
import { OnlineClubTrans, ToasterTrans } from "../utils/translations";
import Class from "../assets/photos/20.png";
import Lib from "../assets/photos/22.png";
import Goose from "../assets/photos/24.png";
import Online from "../assets/photos/23.png";

// Define the context
const AppContext = createContext();

export const AppProvider = ({ children }) => {
  // Initialize theme and language from localStorage or set defaults
  const [theme, setTheme] = useState("light");
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("language") || "russian";
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState(null);
  const [country, setCountry] = useState(() => {
    return localStorage.getItem("country") || "IL";
  });

  useEffect(() => {
    fetch("https://ipwho.is/")
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setLocation({
            country: data.country,
            city: data.city,
            region: data.region,
            country_code: data.country_code,
          });
          if (!localStorage.getItem("country")) {
            if (data?.countryCode === "RU" || data?.countryCode === "IL") {
              setCountry(data?.countryCode);
            } else {
              setCountry("WORLD");
            }
          }
        }
      })
      .catch((err) => {
        console.error("Ошибка получения геолокации:", err);
      });
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Initialize user state
  const [user, setUser] = useState();
  const [token, setToken] = useState();

  const [selectionStates, setSelectionStates] = useState({
    isComplete: false,
    isSaved: false,
  });

  // Update localStorage whenever language changes
  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  useEffect(() => {
    localStorage.setItem("country", country);
  }, [country]);

  // Toggle theme function
  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  // Toggle language function
  const toggleLanguage = (language) => {
    setLanguage(language);
    if (token) {
      updateUser(token, { language })
        .then((updatedUser) => setUser(updatedUser))
        .catch((error) => console.error("Error saving language preference:", error));
    }
  };

  const updateUserFavoritesAndCompletions = useCallback(async (practiceId, isSaved, isComplete) => {
    try {
      const updatedUser = await updateUser(token, {
        savedPractices: isSaved
          ? Array.from(new Set([...user.savedPractices, practiceId]))
          : user.savedPractices.filter(id => id !== practiceId),
        completedPractices: isComplete
          ? Array.from(new Set([...user.completedPractices, practiceId]))
          : user.completedPractices.filter(id => id !== practiceId),
      });
      setUser(updatedUser);
    } catch (error) {
      console.error("Error updating user:", error);
    }
  }, [token, user, setUser]);

  const handleRestrictedAction = (action) => {
    if (!user) {
      setToaster({ message: ToasterTrans[language].login, type: "info", show: true });
    } else {
      action();
    }
  };

  const toggleSaved = (uuid) => {
    setSelectionStates((prev) => {
      const newSavedState = !prev.isSaved;
      updateUserFavoritesAndCompletions(uuid, newSavedState, prev.isComplete);
      return { ...prev, isSaved: newSavedState };
    });
  };

  const toggleComplete = (uuid) => {
    setSelectionStates((prev) => {
      const newCompleteState = !prev.isComplete;
      updateUserFavoritesAndCompletions(uuid, prev.isSaved, newCompleteState);
      return { ...prev, isComplete: newCompleteState };
    });
  };

  // // Update user information function
  // const updateUser = (userData) => {
  //   setUser(userData);
  // };

  function getNextBillingDate(startDate) {
    const start = new Date(startDate);
    const now = new Date();
    const targetDay = start.getDate();

    // начнём с текущего месяца
    let year = now.getFullYear();
    let month = now.getMonth();

    // если сегодня уже день оплаты или позже — переносим на следующий месяц
    if (now.getDate() >= targetDay) {
      month += 1;
    }
    // если переполнили декабрь
    if (month > 11) {
      month = 0;
      year += 1;
    }

    // считаем последний день целевого месяца
    const lastDayOfTargetMonth = new Date(year, month + 1, 0).getDate();
    // если у месяца меньше дней, чем targetDay, берём последний
    const day = Math.min(targetDay, lastDayOfTargetMonth);

    return new Date(year, month, day);
  }

  const [toaster, setToaster] = useState({ message: "", type: "", show: false });

  const onlineClubContent = [
    {
      title: OnlineClubTrans[language].liveLessons,
      description: OnlineClubTrans[language].liveLessonsDescription,
      extra: OnlineClubTrans[language].liveLessonsExtra,
      img: Class,
      button: OnlineClubTrans[language].liveLessonsButton,
      link: "schedule"
    },
    {
      title: OnlineClubTrans[language].library,
      description: OnlineClubTrans[language].libraryDescription,
      extra: OnlineClubTrans[language].libraryExtra,
      img: Lib,
      button: OnlineClubTrans[language].libraryButton,
      link: "/library",
    },
    {
      title: OnlineClubTrans[language].constructor,
      description: OnlineClubTrans[language].constructorDescription,
      extra: OnlineClubTrans[language].constructorExtra,
      img: Online,
      button: OnlineClubTrans[language].constructorButton,
      link: "/constructor-trial",
      featured: true,
    },

    {
      title: OnlineClubTrans[language].chat,
      description: OnlineClubTrans[language].chatDescription,
      extra: OnlineClubTrans[language].chatExtra,
      img: Goose,
      button: OnlineClubTrans[language].chatButton,
      link: "https://t.me/+KpMztbN-sr8xNmMy"
    },
  ];

  const premiumRoles = [
    'premium', 'studio', 'admin','onlineClub'
  ];

  return (
    <AppContext.Provider value={{ theme, toggleTheme, language, toggleLanguage, user, setUser, token, setToken, toaster, setToaster, isMobile, selectionStates, setSelectionStates, handleRestrictedAction, toggleSaved, toggleComplete, getNextBillingDate, loading, setLoading, onlineClubContent, premiumRoles, location, country, setCountry }}>
      {children}
    </AppContext.Provider>
  );
};

// Hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
